import { useEffect, useId, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { Link2, X } from 'lucide-react'
import { toast } from 'sonner'
import { lcCoachingApi } from '@/api/lcCoaching'
import { GrowthAlertContent } from '@/components/growth-alert/GrowthAlertReport'
import type { GrowthAlertPreviewResponse } from '@/types/coaching'
import { getApiErrorCode } from '@/utils/apiError'

export { GrowthAlertContent } from '@/components/growth-alert/GrowthAlertReport'

interface Props {
  preview: GrowthAlertPreviewResponse | null | undefined
  isLoading: boolean
  isError: boolean
  onClose: () => void
}

export function GrowthAlertPreviewDialog({ preview: suppliedPreview, isLoading, isError, onClose }: Props) {
  const { t } = useTranslation('coaching')
  const [refreshedPreview, setRefreshedPreview] = useState<{
    source: Props['preview']
    value: GrowthAlertPreviewResponse
  } | null>(null)
  // A response from a previous learner/date must never replace the new dialog's content.
  const preview = refreshedPreview && refreshedPreview.source === suppliedPreview ? refreshedPreview.value : suppliedPreview
  const titleId = useId()
  const [viewerPhotoUrl, setViewerPhotoUrl] = useState<string | null>(null)
  const [storedShare, setShare] = useState<{ url: string; expiresAt: string; learnerId: number; date: string } | null>(null)
  const share = storedShare?.learnerId === preview?.learner?.learnerId && storedShare?.date === preview?.classSession?.date
    ? storedShare : null
  const [isCreatingShare, setIsCreatingShare] = useState(false)
  const [shareError, setShareError] = useState<string | null>(null)

  const createShare = async () => {
    const learnerId = preview?.learner?.learnerId
    const date = preview?.classSession?.date
    if (!learnerId || !date || isCreatingShare) return

    setIsCreatingShare(true)
    setShareError(null)
    try {
      const result = await lcCoachingApi.createGrowthAlertShare(learnerId, date)
      setShare({ url: `${window.location.origin}${result.path}`, expiresAt: result.expiresAt, learnerId, date })
    } catch (error) {
      const code = getApiErrorCode(error)
      setShareError(code === 'VIDEO_PROCESSING_PENDING' || code === 'VIDEO_PROCESSING_FAILED' || code === 'VIDEO_UNAVAILABLE'
        ? t(`apiError.${code}`, { ns: 'common' })
        : t('growthAlert.shareError'))
    } finally {
      setIsCreatingShare(false)
    }
  }

  const copyShareUrl = async () => {
    if (!share) return
    try {
      await navigator.clipboard.writeText(share.url)
      toast.success(t('growthAlert.copySuccess'))
    } catch {
      setShareError(t('growthAlert.shareError'))
    }
  }

  const handleShareAction = () => {
    if (share) {
      void copyShareUrl()
      return
    }
    void createShare()
  }

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (viewerPhotoUrl !== null) {
        setViewerPhotoUrl(null)
      } else {
        onClose()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [viewerPhotoUrl, onClose])

  const videos = preview?.lessons.flatMap(lesson => lesson.explain.videos ?? []) ?? []
  const videoBlockMessage = videos.some(video => video.status === 'FAILED')
    ? t('growthAlert.videoFailed')
    : videos.some(video => video.status !== 'READY') ? t('growthAlert.videoProcessing')
      : videos.some(video => !video.playbackUrl) ? t('growthAlert.videoUnavailable') : null
  const hasPendingVideo = videos.some(video => ['RESERVED', 'UPLOADING', 'PROCESSING'].includes(video.status) || (video.status === 'READY' && !video.playbackUrl))
  const learnerId = preview?.learner?.learnerId
  const sessionDate = preview?.classSession?.date
  useEffect(() => {
    if (!hasPendingVideo || !learnerId || !sessionDate) return
    let disposed = false
    let refreshing = false
    const timer = window.setInterval(() => {
      if (refreshing || document.visibilityState === 'hidden') return
      refreshing = true
      lcCoachingApi.getGrowthAlertPreview(learnerId, sessionDate)
        .then(next => { if (!disposed) setRefreshedPreview({ source: suppliedPreview, value: next }) })
        .catch(() => { if (!disposed) setShareError(t('growthAlert.error')) })
        .finally(() => { refreshing = false })
    }, 5000)
    return () => { disposed = true; window.clearInterval(timer) }
  }, [hasPendingVideo, learnerId, sessionDate, suppliedPreview, t])
  const canCreateShare = Boolean(
    preview?.available && preview.learner?.learnerId && preview.classSession?.date && !videoBlockMessage,
  )

  const refreshVideoUrl = async (id: string) => {
    if (!preview?.learner || !preview.classSession) return undefined
    const latest = await lcCoachingApi.getGrowthAlertPreview(preview.learner.learnerId, preview.classSession.date)
    return latest.lessons.flatMap(lesson => lesson.explain.videos ?? []).find(video => video.id === id)?.playbackUrl ?? undefined
  }

  const dialog = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-2 sm:p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[94vh] w-full max-w-[696px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="flex h-[68px] shrink-0 items-center justify-between gap-3 border-b border-black/10 bg-white px-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <img
              src="/report/diagnostic/logo_kor.svg"
              width={80}
              height={16}
              alt={t('growthAlert.brandAlt')}
              className="h-4 w-20 shrink-0"
            />
            {preview?.organizationName ? (
              <>
                <span className="h-4 w-px shrink-0 bg-[#d9d9d9]" aria-hidden="true" />
                <span className="truncate text-xs font-bold text-[#888] sm:text-sm">
                  {preview.organizationName}
                </span>
              </>
            ) : null}
            <h2 id={titleId} className="sr-only">{t('growthAlert.title')}</h2>
          </div>

          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
            <button
              type="button"
              onClick={handleShareAction}
              disabled={!canCreateShare || isCreatingShare}
              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-[#4599ff] px-3 text-xs font-bold text-white transition-colors hover:bg-[#3289ef] disabled:cursor-not-allowed disabled:opacity-45 sm:px-4 sm:text-sm"
            >
              <Link2 className="size-4" aria-hidden="true" />
              <span className="hidden min-[420px]:inline">
                {isCreatingShare
                  ? t('growthAlert.creatingUrl')
                  : share
                    ? t('growthAlert.copyUrl')
                    : t('growthAlert.createUrl')}
              </span>
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('growthAlert.close')}
              className="flex size-9 items-center justify-center rounded-lg text-[#888] transition-colors hover:bg-[#f4f4f4] hover:text-[#131313]"
            >
              <X className="size-5" aria-hidden="true" />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto bg-[#f7f7f7]">
          <div className="mx-auto w-full max-w-[672px]">
            <GrowthAlertContent
              preview={preview}
              isLoading={isLoading}
              isError={isError}
              t={t}
              onOpenPhoto={setViewerPhotoUrl}
              surface="dialog"
              onRefreshVideoUrl={refreshVideoUrl}
            />

            {share ? (
              <section className="bg-[#f7f7f7] px-5 pb-8 pt-5 md:px-8">
                <div className="rounded-2xl border border-[#dceaff] bg-white p-4 shadow-[0_4px_8px_rgba(0,0,0,0.04)]">
                  <label
                    htmlFor={`${titleId}-share-url`}
                    className="text-sm font-bold text-[#131313]"
                  >
                    {t('growthAlert.shareUrl')}
                  </label>
                  <input
                    id={`${titleId}-share-url`}
                    readOnly
                    value={share.url}
                    onFocus={(event) => event.currentTarget.select()}
                    className="mt-2 w-full rounded-lg border border-[#e5e5e5] bg-[#f7f7f7] px-3 py-2 text-sm text-[#555]"
                  />
                  <p className="mt-2 text-xs text-[#888]">
                    {t('growthAlert.expiresAt', {
                      date: format(new Date(share.expiresAt), 'yyyy.MM.dd HH:mm'),
                    })}
                  </p>
                </div>
              </section>
            ) : null}
            {videoBlockMessage ? <p className="px-8 pb-6 text-sm text-destructive" role="status">{videoBlockMessage}</p> : null}
            {shareError ? (
              <p className="px-8 pb-6 text-sm text-red-600" role="alert">
                {shareError}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <>
      {createPortal(dialog, document.body)}
      {viewerPhotoUrl !== null
        ? createPortal(
            <ImageViewer
              url={viewerPhotoUrl}
              label={t('growthAlert.imageViewer')}
              onClose={() => setViewerPhotoUrl(null)}
            />,
            document.body,
          )
        : null}
    </>
  )
}

function ImageViewer({ url, label, onClose }: { url: string; label: string; onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/75 p-4"
      onClick={onClose}
    >
      <img
        src={url}
        alt={label}
        className="max-h-[90vh] max-w-[90vw] rounded-xl object-contain"
        onClick={(event) => event.stopPropagation()}
      />
    </div>
  )
}
